import { useReactiveVar } from "@apollo/client";
import React, { useCallback } from "react";

import StatusSettingsView from "./StatusSettingsView";
import { useStatusOperations } from "@/features/knowledge-graph/hooks/useStatusOperations";

import {
  showStatusUpdatesVar,
  statusDisplayConfigVar,
} from "@/stores/knowledgeGraph";

/**
 *
 */
interface StatusSettingsContainerProps {
  visible: boolean;
  onClose: () => void;
}

/**
 * Status settings container component
 * @remarks Uses Apollo reactive variables for state management
 * @param root0 - Component props
 * @param root0.visible - Whether the settings modal is visible
 * @param root0.onClose - Callback when the settings modal is closed
 */
const StatusSettingsContainer: React.FC<StatusSettingsContainerProps> = ({
  visible,
  onClose,
}) => {
  const showStatusUpdates = useReactiveVar(showStatusUpdatesVar);
  const statusDisplayConfig = useReactiveVar(statusDisplayConfigVar);
  const { toggleShowStatusUpdates, updateStatusDisplayConfig } =
    useStatusOperations();

  const handleToggleStatusUpdates = useCallback(() => {
    toggleShowStatusUpdates();
  }, [toggleShowStatusUpdates]);

  const handleUpdateStatusConfig = useCallback(
    (config: Parameters<typeof updateStatusDisplayConfig>[0]) => {
      updateStatusDisplayConfig(config);
    },
    [updateStatusDisplayConfig]
  );

  const handleProgressChange = useCallback(
    (value: boolean) => {
      handleUpdateStatusConfig({ showProgress: value });
    },
    [handleUpdateStatusConfig]
  );

  const handleTimestampsChange = useCallback(
    (value: boolean) => {
      handleUpdateStatusConfig({ showTimestamps: value });
    },
    [handleUpdateStatusConfig]
  );

  const handleDurationsChange = useCallback(
    (value: boolean) => {
      handleUpdateStatusConfig({ showDurations: value });
    },
    [handleUpdateStatusConfig]
  );

  const handleCompactModeChange = useCallback(
    (value: boolean) => {
      handleUpdateStatusConfig({ compactMode: value });
    },
    [handleUpdateStatusConfig]
  );

  return (
    <StatusSettingsView
      visible={visible}
      showStatusUpdates={showStatusUpdates}
      statusDisplayConfig={statusDisplayConfig}
      onClose={onClose}
      onToggleStatusUpdates={handleToggleStatusUpdates}
      onProgressChange={handleProgressChange}
      onTimestampsChange={handleTimestampsChange}
      onDurationsChange={handleDurationsChange}
      onCompactModeChange={handleCompactModeChange}
    />
  );
};

const StatusSettings = React.memo(StatusSettingsContainer);

export default StatusSettings;
